import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Leaf, Edit, Trash2, MapPin, Search, User, UserX, UserCheck, Building2 } from 'lucide-react';

const BoxGrid = ({ 
  boxes, 
  loading, 
  handleEdit, 
  handleDelete, 
  handleApprove,
  handleToggleClaimed,
  categories, 
  vendors,
  onClearFilters 
}) => {
  const getCategoryStyle = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat?.color || 'bg-gray-100 text-gray-700';
  };

  // Helper to get vendor display info
  const getVendorDisplay = (box) => {
    const owner = vendors[box.owner_user_id];
    const claimer = box.claimed_by_user_id ? vendors[box.claimed_by_user_id] : null;
    
    return { owner, claimer };
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-pulse">
          <Leaf className="w-16 h-16 text-green-500 mx-auto mb-4" />
        </div>
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (boxes.length === 0) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600" data-testid="no-boxes-message">No boxes found matching your criteria.</p>
          <Button
            variant="outline"
            onClick={onClearFilters}
            className="mt-4"
            data-testid="clear-filters-no-results-btn"
          >
            Clear Filters
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {boxes.map((box) => {
        const { owner, claimer } = getVendorDisplay(box);
        
        return (
          <Card key={box.id} className={`hover:shadow-xl transition-all duration-300 ${!box.is_active && box.moderation_status !== 'pending' ? 'opacity-60' : ''}`} data-testid={`box-card-${box.id}`}>
            <CardHeader>
              {box.images && box.images.length > 0 && (
                <img
                  src={box.images[0]}
                  alt={box.name}
                  className="w-full h-48 object-cover rounded-lg mb-3"
                  data-testid={`card-image-${box.id}`}
                  onError={(e) => {
                    console.error(`Failed to load image for box ${box.id}:`, box.images[0]);
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <CardTitle className="flex items-center justify-between" data-testid={`card-name-${box.id}`}>
                <span>{box.name}</span>
                <div className="flex gap-1 flex-wrap justify-end">
                  {box.moderation_status === 'pending' && (
                    <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">Pending</Badge>
                  )}
                  {!box.is_active && box.moderation_status !== 'pending' && (
                    <Badge variant="secondary" className="text-xs">Inactive</Badge>
                  )}
                  {box.is_claimed === false && (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200">Unclaimed</Badge>
                  )}
                </div>
              </CardTitle>
              <CardDescription>
                <Badge className={getCategoryStyle(box.category)}>
                  {categories.find(cat => cat.value === box.category)?.label}
                </Badge>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-3" data-testid={`card-description-${box.id}`}>{box.description}</p>
              <div className="flex items-start gap-2 text-sm text-gray-500 mb-3">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span data-testid={`card-address-${box.id}`}>{box.address}</span>
              </div>
              
              {/* Owner/Vendor Information */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2" data-testid={`vendor-info-${box.id}`}>
                {owner ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div>
                      <span className="text-gray-500">Owner:</span>{' '}
                      <span className="font-medium text-gray-800" data-testid={`owner-name-${box.id}`}>
                        {owner.display_name || owner.email}
                      </span>
                      {owner.business_name && (
                        <span className="text-xs text-gray-500 ml-1">({owner.email})</span>
                      )}
                    </div>
                  </div>
                ) : box.owner_user_id ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <User className="w-4 h-4 flex-shrink-0" />
                    <span>Owner: <span className="italic">Unknown vendor</span></span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <UserX className="w-4 h-4 flex-shrink-0" />
                    <span>No owner assigned</span>
                  </div>
                )}
                
                {box.is_claimed !== false && claimer && claimer.email !== owner?.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <UserCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <div>
                      <span className="text-gray-500">Claimed by:</span>{' '}
                      <span className="font-medium text-gray-800">
                        {claimer.display_name || claimer.email}
                      </span>
                    </div>
                  </div>
                )}
                
                {box.is_claimed === false && (
                  <div className="flex items-center gap-2 text-sm text-amber-600">
                    <UserX className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium">Awaiting vendor to claim</span>
                  </div>
                )}
              </div>
              
              {/* Action buttons - row 1 */}
              <div className="flex gap-2 mb-2">
                {box.moderation_status === 'pending' && (
                  <Button
                    onClick={() => handleApprove(box)}
                    variant="default"
                    size="sm"
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    data-testid={`approve-btn-${box.id}`}
                  >
                    <Leaf className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                )}
                <Button
                  onClick={() => handleEdit(box)}
                  variant="outline"
                  size="sm"
                  className="flex-1 border-green-500 text-green-700 hover:bg-green-50"
                  data-testid={`edit-btn-${box.id}`}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  onClick={() => handleDelete(box.id)}
                  variant="outline"
                  size="sm"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  data-testid={`delete-btn-${box.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
              
              {/* Claimed status toggle button - prominent and easy to find */}
              <Button
                onClick={() => handleToggleClaimed(box)}
                variant="outline"
                size="sm"
                className={`w-full ${
                  box.is_claimed === false 
                    ? 'border-green-500 text-green-700 hover:bg-green-50' 
                    : 'border-amber-500 text-amber-700 hover:bg-amber-50'
                }`}
                data-testid={`toggle-claimed-btn-${box.id}`}
              >
                {box.is_claimed === false ? (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" />
                    Mark as Claimed
                  </>
                ) : (
                  <>
                    <UserX className="w-4 h-4 mr-2" />
                    Mark as Unclaimed
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default BoxGrid;
